/**
 * OrchestrationPipeline - visual representation of Nuvra's real build pipeline.
 *
 * Shows the actual 8-stage autonomous build DAG:
 *   Architect → Schema → Backend → Frontend → Render → Repair → Deploy → Publish
 *
 * Connected to real execution states when available.
 * Static when no build is active. No fabricated telemetry.
 */

import { useEffect, useState } from "react";
import { motion }               from "framer-motion";
import { useI18n }              from "@/i18n/I18nContext";
import {
  Cpu, Database, Server, Layers, Eye, Wrench,
  Rocket, Store, CheckCircle2, ArrowRight, Circle,
} from "lucide-react";

/* ── Pipeline stage definitions (matches buildExecutionEngine DAG) ── */

export interface PipelineStage {
  id:      string;
  label:   string;
  caption: string;
  icon:    React.ElementType;
  color:   string;
  bg:      string;
}

const STAGE_LABEL_KEYS: Record<string, string> = {
  architect:  "op.stageArchitect",
  schema:     "op.stageSchema",
  backend:    "op.stageBackend",
  frontend:   "op.stageFrontend",
  ui_render:  "op.stagePreview",
  self_heal:  "op.stageValidation",
  deployment: "op.stageDeployment",
  publish:    "op.stagePublishing",
};

const STAGE_CAPTION_KEYS: Record<string, string> = {
  architect:  "op.captionArchitect",
  schema:     "op.captionSchema",
  backend:    "op.captionBackend",
  frontend:   "op.captionFrontend",
  ui_render:  "op.captionPreview",
  self_heal:  "op.captionValidation",
  deployment: "op.captionDeployment",
  publish:    "op.captionPublishing",
};

export const PIPELINE_STAGES: PipelineStage[] = [
  {
    id:      "architect",
    label:   "Architecture",
    caption: "Intent → system blueprint",
    icon:    Cpu,
    color:   "text-violet-400",
    bg:      "bg-violet-500/10",
  },
  {
    id:      "schema",
    label:   "Database",
    caption: "Schema generation",
    icon:    Database,
    color:   "text-cyan-400",
    bg:      "bg-cyan-500/10",
  },
  {
    id:      "backend",
    label:   "Backend",
    caption: "API routes + services",
    icon:    Server,
    color:   "text-blue-400",
    bg:      "bg-blue-500/10",
  },
  {
    id:      "frontend",
    label:   "Frontend",
    caption: "UI components + pages",
    icon:    Layers,
    color:   "text-emerald-400",
    bg:      "bg-emerald-500/10",
  },
  {
    id:      "ui_render",
    label:   "Preview",
    caption: "Live container",
    icon:    Eye,
    color:   "text-amber-400",
    bg:      "bg-amber-500/10",
  },
  {
    id:      "self_heal",
    label:   "Validation",
    caption: "Auto-repair on failure",
    icon:    Wrench,
    color:   "text-orange-400",
    bg:      "bg-orange-500/10",
  },
  {
    id:      "deployment",
    label:   "Deployment",
    caption: "Production push",
    icon:    Rocket,
    color:   "text-rose-400",
    bg:      "bg-rose-500/10",
  },
  {
    id:      "publish",
    label:   "Publishing",
    caption: "Store submission",
    icon:    Store,
    color:   "text-purple-400",
    bg:      "bg-purple-500/10",
  },
];

type StageStatus = "idle" | "running" | "done" | "failed";

interface StageState {
  stageId: string;
  status:  StageStatus;
}

interface Props {
  /** Pass live execution state if available. Omit for static mode. */
  activeStages?: StageState[];
  /** Variant: "landing" shows full cards, "compact" shows a thin rail */
  variant?: "landing" | "compact";
}

/* ── Component ─────────────────────────────────────────────────── */

export function OrchestrationPipeline({ activeStages, variant = "landing" }: Props) {
  const [demoStep, setDemoStep] = useState(-1);

  useEffect(() => {
    if (activeStages) return;
    let step = -1;
    const tick = setInterval(() => {
      step = (step + 1) % (PIPELINE_STAGES.length + 2);
      setDemoStep(step);
    }, 900);
    return () => clearInterval(tick);
  }, [activeStages]);

  function getStatus(stage: PipelineStage, idx: number): StageStatus {
    if (activeStages) {
      return activeStages.find(s => s.stageId === stage.id)?.status ?? "idle";
    }
    if (demoStep < 0)         return "idle";
    if (idx < demoStep - 1)   return "done";
    if (idx === demoStep - 1) return "running";
    return "idle";
  }

  if (variant === "compact") return <CompactRail activeStages={activeStages} demoStep={demoStep} />;

  return (
    <>
      {/* Mobile: compact wrapping rail */}
      <div className="block md:hidden">
        <CompactRail activeStages={activeStages} demoStep={demoStep} />
      </div>

      {/* Desktop: full horizontal card pipeline */}
      <div className="hidden md:block w-full overflow-x-auto">
        <div className="flex items-start gap-0 min-w-max mx-auto px-2 py-4">
          {PIPELINE_STAGES.map((stage, idx) => {
            const status = getStatus(stage, idx);
            return (
              <div key={stage.id} className="flex items-center">
                <StageCard stage={stage} status={status} idx={idx} />
                {idx < PIPELINE_STAGES.length - 1 && (
                  <Connector active={status === "done" || status === "running"} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ── Stage card ────────────────────────────────────────────────── */

function StageCard({
  stage,
  status,
  idx,
}: {
  stage:  PipelineStage;
  status: StageStatus;
  idx:    number;
}) {
  const { t } = useI18n();
  const Icon = stage.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      className={[
        "relative flex flex-col items-center gap-2 px-3 py-3 rounded-xl border w-[88px] transition-all duration-500",
        status === "running"
          ? `${stage.bg} border-current/40 ${stage.color}`
          : status === "done"
            ? "bg-emerald-500/8 border-emerald-500/25"
            : "bg-muted/20 border-border/20",
      ].join(" ")}
    >
      {/* Status indicator */}
      <div className="absolute -top-1.5 -right-1.5">
        {status === "done" ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
        ) : status === "running" ? (
          <motion.div
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
          >
            <Circle className={`h-3 w-3 fill-current ${stage.color}`} />
          </motion.div>
        ) : null}
      </div>

      {/* Icon */}
      <div className={`p-2 rounded-lg ${stage.bg} transition-all duration-300`}>
        <Icon className={`h-4 w-4 ${
          status === "done" ? "text-emerald-400" :
          status === "running" ? stage.color : "text-muted-foreground/40"
        }`} />
      </div>

      {/* Labels */}
      <div className="text-center space-y-0.5">
        <p className={`text-[10px] font-semibold leading-tight transition-colors ${
          status === "running" ? stage.color :
          status === "done"    ? "text-emerald-400" : "text-muted-foreground/50"
        }`}>
          {t(STAGE_LABEL_KEYS[stage.id] ?? stage.id)}
        </p>
        <p className="text-[8px] text-muted-foreground/30 leading-tight">{t(STAGE_CAPTION_KEYS[stage.id] ?? stage.id)}</p>
      </div>
    </motion.div>
  );
}

/* ── Connector arrow ───────────────────────────────────────────── */

function Connector({ active }: { active: boolean }) {
  return (
    <div className="flex items-center h-[72px] -mx-0.5 z-10">
      <div className={`h-px w-5 transition-all duration-500 ${
        active ? "bg-primary/40" : "bg-border/20"
      }`} />
      <ArrowRight className={`h-3 w-3 transition-all duration-500 -mx-0.5 ${
        active ? "text-primary/40" : "text-border/20"
      }`} />
    </div>
  );
}

/* ── Compact rail variant ──────────────────────────────────────── */

function CompactRail({
  activeStages,
  demoStep,
}: {
  activeStages?: StageState[];
  demoStep: number;
}) {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-1.5 py-2">
      {PIPELINE_STAGES.map((stage, idx) => {
        const status: StageStatus = activeStages
          ? activeStages.find(s => s.stageId === stage.id)?.status ?? "idle"
          : idx < demoStep - 1 ? "done"
          : idx === demoStep - 1 ? "running"
          : "idle";

        return (
          <div
            key={stage.id}
            title={t(STAGE_CAPTION_KEYS[stage.id] ?? stage.id)}
            className={`flex flex-col items-center gap-1 px-2 py-2 rounded-xl border transition-all ${
              status === "running"
                ? `${stage.bg} ${stage.color} border-current/30`
                : status === "done"
                  ? "bg-emerald-500/8 text-emerald-400 border-emerald-500/20"
                  : "bg-muted/10 text-muted-foreground/25 border-border/10"
            }`}
          >
            <stage.icon className={`h-3.5 w-3.5 ${
              status === "running" ? stage.color :
              status === "done"    ? "text-emerald-400" : "text-muted-foreground/25"
            }`} />
            <span className={`text-[9px] font-medium text-center leading-tight ${
              status === "running" ? stage.color :
              status === "done"    ? "text-emerald-400" : "text-muted-foreground/25"
            }`}>{t(STAGE_LABEL_KEYS[stage.id] ?? stage.id)}</span>
          </div>
        );
      })}
    </div>
  );
}
